// Added by Sejune Oh on 2019-03-11
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC } from 'react'
import {
    createStyles,
    withStyles,
    WithStyles,
    Theme,
    WithTheme,
    OutlinedInput,
    Input,
    FilledInput,
    InputBaseComponentProps,
} from '@material-ui/core'
import classNames from 'classnames'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { TextFormatter, ReactSelectEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime elements
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@/components/_primes'
import { LIGHT_GREY } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface SelectBaseFCProps {
    id: string
    value?: string | number
    label: string
    margin?: 'normal' | 'dense'
    isBoolean?: boolean
    menuItems: Array<any>
    isFullWidth?: boolean
    isFitContent?: boolean
    isRequired?: boolean
    onChange?: ReactSelectEvent
    itemFormatter?: TextFormatter<any>
    isEmptyOptionValid?: boolean
    disabled?: boolean
    keyProps?: string
    hidden?: boolean
    variant?: 'standard' | 'outlined' | 'filled'
    inline?: string
    inputProps?: InputBaseComponentProps
}

interface SelectFCProps extends SelectBaseFCProps, WithStyles<typeof styles>, WithTheme {
    description?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const SelectElement: FC<SelectFCProps> = ({ classes, keyProps, ...props }) => {
    const inputProps = {
        name: 'select',
        id: props.id,
        itemType: props.isBoolean ? 'boolean' : 'string',
        disabled: props.disabled,
    }
    const tempInput =
        props.variant === 'outlined' ? (
            <OutlinedInput labelWidth={0} {...inputProps} />
        ) : props.variant === 'filled' ? (
            <FilledInput {...inputProps} />
        ) : (
            <Input {...inputProps} />
        )
    const hasDescription = props.description && props.description !== ''

    return (
        <div
            className={classNames(
                classes.SelectWrapper,
                props.isFullWidth ? classes.SelectWrapperFull : '',
                props.hidden ? classes.SelectWrapperHidden : '',
                props.isFitContent ? classes.SelectWrapperFitContent : '',
                props.inline ? classes.SelectWrapperInline : classes.SelectWrapperBlock,
                hasDescription ? classes.SelectWrapperWithDesc : ''
            )}
        >
            <FormControl
                className={classes.SelectField}
                fullWidth={props.isFullWidth}
                variant={props.variant}
                margin={props.margin || 'normal'}
            >
                <InputLabel
                    className={classNames(classes.InpulLabel, props.disabled ? classes.DisabledInputLabel : '')}
                >
                    {props.label}
                    {props.isRequired ? '  *' : ''}
                </InputLabel>
                <Select
                    className={classNames(props.disabled ? classes.SelectDislabed : '')}
                    value={props.value}
                    onChange={props.onChange}
                    variant={props.variant}
                    input={tempInput}
                    required={props.isRequired}
                    inputProps={props.inputProps}
                    data-cy-select={props.id}
                >
                    {props.isEmptyOptionValid && <MenuItem value="">None</MenuItem>}
                    {props.menuItems.map((menu, index) => (
                        <MenuItem key={index} value={menu[keyProps!] || menu}>
                            {props.itemFormatter ? props.itemFormatter(menu) : menu}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText id="component-input" />
                {hasDescription && <span className={classes.SelectDescriptionWrapper}>{props.description}</span>}
            </FormControl>
        </div>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        SelectWrapper: {
            padding: `${theme.spacing()}px ${theme.spacing()}px 0 ${theme.spacing()}px`,
            width: '50%',
            flexGrow: 0.5,
            boxSizing: 'border-box',
            position: 'relative',
            '@media (max-width: 990px)': {
                width: '100%',
                flexGrow: 1,
            },
        },
        SelectWrapperFull: {
            width: '100%',
            flexGrow: 1,
            boxSizing: 'border-box',
        },
        SelectWrapperWithDesc: {
            paddingBottom: `${theme.spacing() / 2}px`,
        },
        SelectField: {
            margin: `${theme.spacing()}px 0 0 0`,
            width: '100%',
            '&>label': {
                left: `${theme.spacing()}px`,
            },
        },
        SelectWrapperHidden: {
            display: 'none',
        },
        SelectDislabed: {
            color: LIGHT_GREY,
            '&:before': {
                borderBottomColor: LIGHT_GREY,
            },
        },
        SelectWrapperFitContent: {
            width: 'fit-content',
        },
        SelectWrapperInline: {
            display: 'inline-flex',
        },
        SelectWrapperBlock: {
            display: 'flex',
        },
        InpulLabel: {
            color: '#fff',
            fontSize: '1.3rem',
            fontWeight: 'normal',
            '&.Mui-focused': {
                fontWeight: 'bold'
            }
        },
        SelectDescriptionWrapper: {
            color: 'rgba(255, 255, 255, 0.7)',
            width: 'max-content',
            left: '3px',
            right: '-100%',
            bottom: '0',
            '@media (max-width: 990px)': {
                right: 0,
            },
        },
        DisabledInputLabel: {
            color: 'rgba(255, 255, 255, 0.3)',
        },
    })

export default withStyles(styles, { withTheme: true })(SelectElement)
